/** The server's refusal while the computer's boot lease is held, which a teammate's own run holds first. */
export function isComputerBusyError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return code === "CONFLICT" && message === "Computer is busy";
}

/**
 * Boots a computer, waiting out a busy answer. A first-run demo opens the screen while the run it
 * just started is provisioning the same computer, so "busy" there is a short wait, not a failure.
 * Bounded: after the attempts are spent the last error surfaces.
 */
export async function bootWaitingForBusy<T>(
  boot: () => Promise<T>,
  options: {
    attempts?: number;
    delayMs?: number;
    sleep?: (ms: number) => Promise<void>;
    isCancelled?: () => boolean;
  } = {},
): Promise<T> {
  const {
    attempts = 8,
    delayMs = 2000,
    sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
  } = options;
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await boot();
    } catch (error) {
      if (!isComputerBusyError(error) || attempt >= attempts || options.isCancelled?.())
        throw error;
      await sleep(delayMs);
    }
  }
}
